import { supabase } from '@/lib/supabase';
import {
  ANALYTICS_RANK_LIMIT,
  AgentSummaryRpcRow,
  AnalyticsAgentRow,
  AnalyticsComparisonWindow,
  AnalyticsDiscoverySummary,
  AnalyticsOverview,
  AnalyticsProductRow,
  AnalyticsReliabilitySummary,
  AnalyticsTimeRange,
  DiscoverySummaryRpcRow,
  ReliabilitySummaryRpcRow,
  buildAgentRows,
  buildDiscoverySummary,
  buildProductRows,
  buildReliabilitySummary,
  getAnalyticsDateRange,
  getAnalyticsComparisonRanges,
  normalizeOverview,
  ProductEnrichmentRow,
  ProductSummaryRpcRow,
} from '@/services/analyticsMetrics';

export type TimeRange = '1d' | '7d' | '30d' | '90d' | 'all';
export type FirstPartyAnalyticsRange = AnalyticsTimeRange;

export interface ItemAdditionDataPoint {
  date: string;
  count: number;
}

export interface UserAdditionStats {
  user: string;
  count: number;
}

export interface CategoryStats {
  name: string;
  count: number;
}

export interface AnalyticsSnapshot {
  overview: AnalyticsOverview;
  agents: AnalyticsAgentRow[];
  products: AnalyticsProductRow[];
  discovery: AnalyticsDiscoverySummary;
}

export interface AnalyticsOverviewComparison {
  current: AnalyticsOverview;
  previous: AnalyticsOverview;
  window: AnalyticsComparisonWindow;
}

type AnalyticsErrorLike = {
  code?: string;
  message?: string;
};

type AnalyticsQueryResult<T> = {
  data: T | null;
  error: AnalyticsErrorLike | null;
};

type AnalyticsSupabaseClient = {
  rpc: (name: string, params: Record<string, string>) => Promise<AnalyticsQueryResult<unknown>>;
  from: (table: string) => {
    select: (columns: string) => {
      in: (column: string, values: string[]) => Promise<AnalyticsQueryResult<unknown[]>>;
    };
  };
};

const analyticsSupabase = supabase as unknown as AnalyticsSupabaseClient;

type AnalyticsRpcName =
  | 'get_analytics_overview'
  | 'get_analytics_agent_summary'
  | 'get_analytics_product_summary'
  | 'get_analytics_discovery_summary'
  | 'get_analytics_reliability_summary';

function getRpcParams(timeRange: FirstPartyAnalyticsRange) {
  const range = getAnalyticsDateRange(timeRange);
  return {
    p_start: range.start,
    p_end: range.end,
  };
}

function createAnalyticsError(error: unknown): Error {
  const errorLike = error as AnalyticsErrorLike | null;
  const message = String(errorLike?.message || '');
  const code = String(errorLike?.code || '');

  if (code === '42501' || message.toLowerCase().includes('access denied')) {
    return new Error('This account is not authorized for analytics. Ask the owner to add this Supabase user to analytics_admin_users.');
  }

  if (message.toLowerCase().includes('function') && message.toLowerCase().includes('does not exist')) {
    return new Error('The Analytics V1 RPCs are not available in this Supabase project yet.');
  }

  return new Error(message || 'Failed to load analytics data.');
}

async function callAnalyticsRpc<T>(name: AnalyticsRpcName, timeRange: FirstPartyAnalyticsRange): Promise<T> {
  return callAnalyticsRpcWithParams<T>(name, getRpcParams(timeRange));
}

async function callAnalyticsRpcWithParams<T>(name: AnalyticsRpcName, params: Record<string, string>): Promise<T> {
  const { data, error } = await analyticsSupabase.rpc(name, params);

  if (error) {
    throw createAnalyticsError(error);
  }

  return data as T;
}

export async function getFirstPartyAnalyticsOverview(timeRange: FirstPartyAnalyticsRange): Promise<AnalyticsOverview> {
  const data = await callAnalyticsRpc<Record<string, unknown>>('get_analytics_overview', timeRange);
  return normalizeOverview(data);
}

export async function getFirstPartyAnalyticsOverviewComparison(timeRange: FirstPartyAnalyticsRange): Promise<AnalyticsOverviewComparison> {
  const window = getAnalyticsComparisonRanges(timeRange);
  const [current, previous] = await Promise.all([
    callAnalyticsRpcWithParams<Record<string, unknown>>('get_analytics_overview', {
      p_start: window.current.start,
      p_end: window.current.end,
    }),
    callAnalyticsRpcWithParams<Record<string, unknown>>('get_analytics_overview', {
      p_start: window.previous.start,
      p_end: window.previous.end,
    }),
  ]);

  return {
    current: normalizeOverview(current),
    previous: normalizeOverview(previous),
    window,
  };
}

export async function getAgentAnalytics(timeRange: FirstPartyAnalyticsRange, limit = ANALYTICS_RANK_LIMIT): Promise<AnalyticsAgentRow[]> {
  const data = await callAnalyticsRpc<AgentSummaryRpcRow[]>('get_analytics_agent_summary', timeRange);
  return buildAgentRows(data || [], limit);
}

export async function getProductAnalytics(timeRange: FirstPartyAnalyticsRange, limit = 10): Promise<AnalyticsProductRow[]> {
  const data = await callAnalyticsRpc<ProductSummaryRpcRow[]>('get_analytics_product_summary', timeRange);
  const bounded = buildProductRows(data || [], [], Math.min(limit, ANALYTICS_RANK_LIMIT));
  const productIds = bounded.map((row) => row.productId);

  if (productIds.length === 0) {
    return bounded;
  }

  let productRows: ProductEnrichmentRow[] = [];
  try {
    productRows = await getProductEnrichmentRows(productIds);
  } catch (error) {
    console.warn('Product analytics enrichment failed; showing product IDs only.', error);
  }

  return buildProductRows(data || [], productRows, Math.min(limit, ANALYTICS_RANK_LIMIT));
}

export async function getDiscoveryAnalytics(timeRange: FirstPartyAnalyticsRange, limit = ANALYTICS_RANK_LIMIT): Promise<AnalyticsDiscoverySummary> {
  const data = await callAnalyticsRpc<DiscoverySummaryRpcRow[]>('get_analytics_discovery_summary', timeRange);
  return buildDiscoverySummary(data || [], limit);
}

export async function getReliabilityAnalytics(timeRange: FirstPartyAnalyticsRange, limit = ANALYTICS_RANK_LIMIT): Promise<AnalyticsReliabilitySummary> {
  const data = await callAnalyticsRpc<ReliabilitySummaryRpcRow[]>('get_analytics_reliability_summary', timeRange);
  return buildReliabilitySummary(data || [], limit);
}

export async function getAnalyticsSnapshot(timeRange: FirstPartyAnalyticsRange): Promise<AnalyticsSnapshot> {
  const [overview, agents, products, discovery] = await Promise.all([
    getFirstPartyAnalyticsOverview(timeRange),
    getAgentAnalytics(timeRange),
    getProductAnalytics(timeRange),
    getDiscoveryAnalytics(timeRange),
  ]);

  return {
    overview,
    agents,
    products,
    discovery,
  };
}

async function getProductEnrichmentRows(productIds: string[]): Promise<ProductEnrichmentRow[]> {
  const boundedIds = productIds.slice(0, ANALYTICS_RANK_LIMIT);

  const explicitSelect = await analyticsSupabase
    .from('db')
    .select('id,name,brand,category,"category[0]"')
    .in('id', boundedIds);

  if (!explicitSelect.error) {
    return (explicitSelect.data || []) as unknown as ProductEnrichmentRow[];
  }

  const fallbackSelect = await analyticsSupabase
    .from('db')
    .select('*')
    .in('id', boundedIds);

  if (fallbackSelect.error) {
    throw createAnalyticsError(fallbackSelect.error);
  }

  return (fallbackSelect.data || []) as unknown as ProductEnrichmentRow[];
}

export async function getItemAdditionStats(timeRange: TimeRange): Promise<ItemAdditionDataPoint[]> {
  try {
    // Define the start date based on the selected time range
    let startDate: Date | null = new Date();
    
    if (timeRange === '1d') {
      startDate.setDate(startDate.getDate() - 1);
    } else if (timeRange === '7d') {
      startDate.setDate(startDate.getDate() - 7);
    } else if (timeRange === '30d') {
      startDate.setDate(startDate.getDate() - 30);
    } else if (timeRange === '90d') {
      startDate.setDate(startDate.getDate() - 90);
    } else if (timeRange === 'all') {
      startDate = null;
    }

    let query = supabase
      .from('db')
      .select('created_at');
    
    if (startDate) {
      query = query.gte('created_at', startDate.toISOString());
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching item addition stats:', error);
      throw error;
    }

    // Group the data by date
    const groupedByDate = data?.reduce<Record<string, number>>((acc, item) => {
      // Format the date as YYYY-MM-DD
      const date = new Date(item.created_at).toISOString().split('T')[0];
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {}) || {};

    // Fill in missing dates with zero counts
    const result: ItemAdditionDataPoint[] = [];
    
    if (timeRange !== 'all' && startDate) {
      const endDate = new Date();
      const currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        const dateStr = currentDate.toISOString().split('T')[0];
        result.push({
          date: dateStr,
          count: groupedByDate[dateStr] || 0
        });
        currentDate.setDate(currentDate.getDate() + 1);
      }
    } else {
      // For 'all' just use the available data points
      Object.entries(groupedByDate).forEach(([date, count]) => {
        result.push({ date, count });
      });
      
      // Sort by date
      result.sort((a, b) => a.date.localeCompare(b.date));
    }

    return result;
  } catch (error) {
    console.error('Error in getItemAdditionStats:', error);
    throw error;
  }
}

export async function getUserAdditionStats(timeRange: TimeRange): Promise<UserAdditionStats[]> {
  try {
    // Define the start date based on the selected time range
    let startDate: Date | null = new Date();
    
    if (timeRange === '1d') {
      startDate.setDate(startDate.getDate() - 1);
    } else if (timeRange === '7d') {
      startDate.setDate(startDate.getDate() - 7);
    } else if (timeRange === '30d') {
      startDate.setDate(startDate.getDate() - 30);
    } else if (timeRange === '90d') {
      startDate.setDate(startDate.getDate() - 90);
    } else if (timeRange === 'all') {
      startDate = null;
    }

    let query = supabase
      .from('db')
      .select('created_by');
    
    if (startDate) {
      query = query.gte('created_at', startDate.toISOString());
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching user addition stats:', error);
      throw error;
    }

    // Group the data by user
    const groupedByUser = data?.reduce<Record<string, number>>((acc, item) => {
      const user = item.created_by || 'Unknown user';
      acc[user] = (acc[user] || 0) + 1;
      return acc;
    }, {}) || {};

    // Convert to array and sort by count (descending)
    return Object.entries(groupedByUser)
      .map(([user, count]) => ({ user, count }))
      .sort((a, b) => b.count - a.count);
  } catch (error) {
    console.error('Error in getUserAdditionStats:', error);
    throw error;
  }
}

export async function getCategoryStats(timeRange: TimeRange): Promise<CategoryStats[]> {
  try {
    let query = supabase.from('db').select('*');
    
    if (timeRange !== 'all') {
      // Define the start date based on the selected time range
      const startDate = new Date();
      
      if (timeRange === '1d') {
        startDate.setDate(startDate.getDate() - 1);
      } else if (timeRange === '7d') {
        startDate.setDate(startDate.getDate() - 7);
      } else if (timeRange === '30d') {
        startDate.setDate(startDate.getDate() - 30);
      } else if (timeRange === '90d') {
        startDate.setDate(startDate.getDate() - 90);
      }
      
      query = query.gte('created_at', startDate.toISOString());
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching category stats:', error);
      throw new Error('Failed to fetch category statistics');
    }
    
    const categoryMap = new Map<string, number>();
    
    data?.forEach(item => {
      const category = item.category;
      if (category && typeof category === 'string' && category.trim() !== '') {
        if (categoryMap.has(category)) {
          categoryMap.set(category, categoryMap.get(category)! + 1);
        } else {
          categoryMap.set(category, 1);
        }
      }
    });
    
    return Array.from(categoryMap.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); // Return top 10 categories
    
  } catch (error) {
    console.error('Error in getCategoryStats:', error);
    throw error;
  }
}
